import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { paymentService } from '@/services/paymentService'
import type { PaymentTransaction, PaymentStatus } from '@/types/payment'

interface PaymentStatusPollerProps {
  transactionId: string
  onStatusChange?: (status: PaymentStatus, payment: PaymentTransaction) => void
  onPaymentSuccess?: (payment: PaymentTransaction) => void
  onPaymentFailed?: (payment: PaymentTransaction) => void
  onPaymentExpired?: (payment: PaymentTransaction) => void
  pollInterval?: number // in milliseconds
  showCountdown?: boolean
}

export const PaymentStatusPoller: React.FC<PaymentStatusPollerProps> = ({
  transactionId,
  onStatusChange,
  onPaymentSuccess,
  onPaymentFailed,
  onPaymentExpired,
  pollInterval = 5000,
  showCountdown = true,
}) => {
  const [previousStatus, setPreviousStatus] = useState<PaymentStatus | null>(null)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)

  const { data: payment, isLoading, error } = useQuery({
    queryKey: ['paymentStatus', transactionId],
    queryFn: () => paymentService.getPaymentStatus(transactionId),
    refetchInterval: (query) => {
      // Stop polling if payment is in final state
      const data = query.state.data
      if (data && (data.status === 'paid' || data.status === 'failed' || data.status === 'expired' || data.status === 'refunded')) {
        return false
      }
      return pollInterval
    },
    enabled: !!transactionId,
  })

  // Calculate time remaining
  useEffect(() => {
    if (!payment?.expiryTime) {
      setTimeRemaining(null)
      return
    }

    const calculateTimeRemaining = () => {
      const expiryTime = new Date(payment.expiryTime!).getTime()
      const now = Date.now()
      const remaining = Math.max(0, expiryTime - now)
      setTimeRemaining(remaining)

      // If expired, trigger callback
      if (remaining === 0 && payment.status === 'pending') {
        onPaymentExpired?.(payment)
      }
    }

    calculateTimeRemaining()
    const interval = setInterval(calculateTimeRemaining, 1000)

    return () => clearInterval(interval)
  }, [payment?.expiryTime, payment, onPaymentExpired])

  // Handle status changes
  useEffect(() => {
    if (!payment) return

    if (previousStatus && previousStatus !== payment.status) {
      onStatusChange?.(payment.status, payment)

      switch (payment.status) {
        case 'paid':
          onPaymentSuccess?.(payment)
          break
        case 'failed':
          onPaymentFailed?.(payment)
          break
        case 'expired':
          onPaymentExpired?.(payment)
          break
      }
    }

    setPreviousStatus(payment.status)
  }, [payment, previousStatus, onStatusChange, onPaymentSuccess, onPaymentFailed, onPaymentExpired])

  const formatTimeRemaining = useCallback((ms: number) => {
    if (ms <= 0) return '00:00:00'

    const hours = Math.floor(ms / (1000 * 60 * 60))
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((ms % (1000 * 60)) / 1000)

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }, [])

  const statusConfig = useMemo(() => {
    if (!payment) return null

    switch (payment.status) {
      case 'pending':
        return {
          color: 'bg-yellow-100 border-yellow-200 text-yellow-800',
          icon: '⏳',
          label: 'Waiting for Payment',
          description: 'Complete your payment to continue',
        }
      case 'paid':
        return {
          color: 'bg-green-100 border-green-200 text-green-800',
          icon: '✅',
          label: 'Payment Successful',
          description: 'Your payment has been confirmed',
        }
      case 'failed':
        return {
          color: 'bg-red-100 border-red-200 text-red-800',
          icon: '❌',
          label: 'Payment Failed',
          description: 'Your payment could not be processed',
        }
      case 'expired':
        return {
          color: 'bg-gray-100 border-gray-200 text-gray-800',
          icon: '⏰',
          label: 'Payment Expired',
          description: 'The payment deadline has passed',
        }
      case 'refunded':
        return {
          color: 'bg-blue-100 border-blue-200 text-blue-800',
          icon: '↩️',
          label: 'Refunded',
          description: 'This payment has been refunded',
        }
      default:
        return null
    }
  }, [payment])

  if (isLoading && !payment) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-600 border-t-transparent" />
        <span className="ml-2 text-gray-600">Checking payment status...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">Failed to check payment status. Please refresh the page.</p>
      </div>
    )
  }

  if (!payment || !statusConfig) {
    return null
  }

  return (
    <div className="space-y-4">
      {/* Status Banner */}
      <div className={`p-4 rounded-lg border ${statusConfig.color}`}>
        <div className="flex items-center gap-3">
          <span className="text-2xl">{statusConfig.icon}</span>
          <div>
            <h3 className="font-semibold">{statusConfig.label}</h3>
            <p className="text-sm opacity-80">{statusConfig.description}</p>
          </div>
        </div>
      </div>

      {/* Countdown Timer */}
      {showCountdown && payment.status === 'pending' && timeRemaining !== null && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="text-center">
            <p className="text-sm text-orange-700 mb-1">Time Remaining</p>
            <p className={`text-3xl font-mono font-bold ${
              timeRemaining < 5 * 60 * 1000 ? 'text-red-600' : 'text-orange-800'
            }`}>
              {formatTimeRemaining(timeRemaining)}
            </p>
            {timeRemaining < 5 * 60 * 1000 && timeRemaining > 0 && (
              <p className="text-sm text-red-600 mt-1">
                Payment expiring soon!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Polling Indicator */}
      {payment.status === 'pending' && (
        <div className="flex items-center justify-center text-sm text-gray-500">
          <div className="animate-pulse w-2 h-2 bg-green-500 rounded-full mr-2" />
          Checking for payment confirmation...
        </div>
      )}
    </div>
  )
}

export default PaymentStatusPoller
